import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useDeviceSensors,
    useCreateDeviceSensor,
    useUpdateDeviceSensor,
    useDeleteDeviceSensor,
} from '../../hooks/deviceSensors';
import type { DeviceSensor, DeviceSensorStatus, CreateDeviceSensorDto } from '../../types/types';
import { t } from '../../i18n/t';

const statusLabel = (s: string) => {
    const map: Record<string, string> = {
        active: t('deviceSensors.status.active'),
        inactive: t('deviceSensors.status.inactive'),
    };
    return map[s] ?? s;
};

const DashboardDeviceSensors = () => {
    const { data: sensors = [], isLoading, refetch } = useDeviceSensors();
    const deleteMutation = useDeleteDeviceSensor();
    const [createOpen, setCreateOpen] = useState(false);
    const [editingSensor, setEditingSensor] = useState<DeviceSensor | null>(null);

    const handleDelete = async (sensor: DeviceSensor) => {
        if (!window.confirm(t('deviceSensors.deleteConfirm', { type: sensor.type }))) return;
        try {
            await deleteMutation.mutateAsync(sensor.id);
        } catch (e) {
            console.error(e);
            alert(t('deviceSensors.deleteFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('deviceSensors.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('deviceSensors.new')}
                </Button>
                <Tooltip title={t('deviceSensors.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>{t('deviceSensors.colType')}</TableCell>
                            <TableCell>{t('deviceSensors.colDescription')}</TableCell>
                            <TableCell>{t('deviceSensors.colStatus')}</TableCell>
                            <TableCell align="right">{t('deviceSensors.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sensors.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {t('deviceSensors.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            sensors.map((sensor) => (
                                <TableRow key={sensor.id}>
                                    <TableCell>{sensor.id}</TableCell>
                                    <TableCell>{sensor.type}</TableCell>
                                    <TableCell>{sensor.description}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={statusLabel(sensor.status)}
                                            color={sensor.status === 'active' ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => setEditingSensor(sensor)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(sensor)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <DeviceSensorFormDialog
                    title={t('deviceSensors.dialogNew')}
                    onClose={() => setCreateOpen(false)}
                />
            )}
            {editingSensor && (
                <DeviceSensorFormDialog
                    title={t('deviceSensors.dialogEdit')}
                    sensor={editingSensor}
                    onClose={() => setEditingSensor(null)}
                />
            )}
        </Box>
    );
};

interface DeviceSensorFormDialogProps {
    title: string;
    sensor?: DeviceSensor;
    onClose: () => void;
}

const DeviceSensorFormDialog: React.FC<DeviceSensorFormDialogProps> = ({ title, sensor, onClose }) => {
    const createMutation = useCreateDeviceSensor();
    const updateMutation = useUpdateDeviceSensor();
    const isEdit = sensor != null;

    const [type, setType] = useState(sensor?.type ?? '');
    const [description, setDescription] = useState(sensor?.description ?? '');
    const [status, setStatus] = useState<DeviceSensorStatus>(sensor?.status ?? 'active');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!type.trim()) {
            alert(t('deviceSensors.enterType'));
            return;
        }
        if (!description.trim()) {
            alert(t('deviceSensors.enterDescription'));
            return;
        }

        const dto: CreateDeviceSensorDto = {
            type: type.trim(),
            description: description.trim(),
            status,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await updateMutation.mutateAsync({ id: sensor.id, dto });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('deviceSensors.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('deviceSensors.fieldType')}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('deviceSensors.fieldDescription')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                        size="small"
                        fullWidth
                    />
                    <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value as DeviceSensorStatus)}>
                        <FormControlLabel
                            value="active"
                            control={<Radio />}
                            label={t('deviceSensors.status.active')}
                        />
                        <FormControlLabel
                            value="inactive"
                            control={<Radio />}
                            label={t('deviceSensors.status.inactive')}
                        />
                    </RadioGroup>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardDeviceSensors;
